import React from 'react';
import { Select, MenuItem, SelectChangeEvent } from '@mui/material';
import { IconTextContent } from './IconTextContent';
import { withTooltip } from './tooltipHelpers';

export interface DropdownOption<T> {
  /** The value of the option */
  value: T;
  /** The display label for the option */
  label: React.ReactNode;
  /** Whether this option is currently loading */
  loading?: boolean;
  /** Unique identifier for the option */
  id?: string | number;
}

export interface IconOverTextDropdownProps<T = string> {
  /** The icon to display above the text */
  icon: React.ReactNode;
  /** The text to display below the icon */
  text: string;
  /** The tooltip to show on hover */
  tooltip?: string;
  /** Additional sx styles to apply */
  sx?: object;
  /** Whether to show hover background effect */
  showHoverEffect?: boolean;
  /** Wrap in span for disabled tooltip support */
  wrapInSpan?: boolean;
  /** Array of dropdown options */
  options: DropdownOption<T>[];
  /** Function to filter options (optional, to exclude certain values) */
  filterOptions?: (option: DropdownOption<T>) => boolean;
  /** Whether the dropdown is disabled */
  disabled?: boolean;
  /** Callback when an option is selected */
  onSelect: (value: T) => void;
  /** Additional sx styles for the text in the button */
  textColor?: string;
  /** Font weight for the text in the button */
  fontWeight?: number;
}

/**
 * A generic dropdown button component that displays an icon above text
 * Can be used for any dropdown actions like "Move Division", "Filter", etc.
 */
export const IconOverTextDropdown = <T,>({
  icon,
  text,
  tooltip,
  sx,
  showHoverEffect = true,
  wrapInSpan = false,
  options,
  filterOptions,
  disabled = false,
  onSelect,
  textColor = '#000000DF',
  fontWeight = 300,
}: IconOverTextDropdownProps<T>) => {
  // Convert the generic value to string for Select component
  const handleChange = (event: SelectChangeEvent) => {
    const selectedOption = options.find(opt => String(opt.value) === event.target.value);
    if (selectedOption) {
      onSelect(selectedOption.value);
    }
  };

  // Filter options if filter function is provided
  const filteredOptions = filterOptions ? options.filter(filterOptions) : options;

  const selectComponent = (
    <Select
      displayEmpty
      value=''
      onChange={handleChange}
      disabled={disabled}
      renderValue={() => <IconTextContent icon={icon} text={text} textColor={textColor} fontWeight={fontWeight} />}
      sx={{
        textTransform: 'none',
        px: 1,
        py: 1,
        pb: '10px !important',
        minWidth: 'auto',
        display: 'flex',
        alignItems: 'center',
        color: 'rgba(0, 0, 0, 0.87) !important',
        // Hover effect
        ...(showHoverEffect && {
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
        }),
        // No hover effect
        ...(!showHoverEffect && {
          '&:hover': {
            backgroundColor: 'transparent',
          },
        }),
        '&.Mui-disabled': {
          color: 'rgba(0, 0, 0, 0.38) !important',
        },
        '& .MuiSelect-select': {
          padding: '0px 24px 0px 8px !important',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        },
        '& .MuiSelect-icon': {
          color: theme => (disabled ? theme.palette.text.disabled : 'rgba(0, 0, 0, 0.54)'),
          right: 8,
        },
        '& fieldset': {
          border: 'none',
        },
        ...sx,
      }}
    >
      {filteredOptions.map((option, index) => (
        <MenuItem key={option.id ?? index} value={String(option.value)}>
          {option.label}
        </MenuItem>
      ))}
    </Select>
  );

  return withTooltip(selectComponent, tooltip, wrapInSpan);
};

export default IconOverTextDropdown;
